import type { ComponentConfigV2, VideoOptions, VideoSource } from '@/types/configurationV2'

interface VideoOptionsEditorProps {
  component: ComponentConfigV2
  onUpdate: (updated: ComponentConfigV2) => void
}

export default function VideoOptionsEditor({ component, onUpdate }: VideoOptionsEditorProps) {
  const options = component.options as VideoOptions

  const update = (patch: Partial<VideoOptions>) => {
    onUpdate({ ...component, options: { ...options, ...patch } })
  }

  const updateLessonSource = (patch: Partial<Extract<VideoSource, { type: 'lesson' }>>) => {
    if (options.source.type === 'lesson') {
      update({ source: { ...options.source, ...patch } })
    }
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={options.pauseEnabled}
            onChange={(e) => update({ pauseEnabled: e.target.checked })}
          />
          Pause & Quiz 활성화
        </label>

        {options.pauseEnabled && (
          <div className="mt-2">
            <div className="text-xs text-gray-500 mb-1">1회차 Pause 모드</div>
            {(['guide', 'solve'] as const).map((v) => (
              <label key={v} className="flex items-center gap-1.5 text-xs">
                <input
                  type="radio"
                  name={`pauseFirstWatch-${component.id}`}
                  checked={options.pauseFirstWatchMode === v}
                  onChange={() => update({ pauseFirstWatchMode: v })}
                />
                {v === 'guide' ? '책 안내 (학생 토글 허용)' : '바로 풀기 (태블릿 모드)'}
              </label>
            ))}
          </div>
        )}

        <div>
          <div className="text-xs text-gray-500 mb-1">영상 소스</div>
          <select
            value={options.source.type}
            onChange={(e) => {
              const type = e.target.value as VideoSource['type']
              if (type === 'lesson') update({ source: { type: 'lesson', contentScope: 'current', subtype: 'main' } })
              else if (type === 'library') update({ source: { type: 'library', resourceId: 0 } })
              else update({ source: { type: 'url', url: '' } })
            }}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
          >
            <option value="lesson">Lesson 영상</option>
            <option value="library">자료실</option>
            <option value="url">URL 직접입력</option>
          </select>

          {options.source.type === 'lesson' && (
            <div className="mt-2 flex gap-2">
              <select
                value={options.source.contentScope}
                onChange={(e) => updateLessonSource({ contentScope: e.target.value as 'current' | 'previous' | 'next' })}
                className="px-2 py-1 text-xs border border-gray-300 rounded"
              >
                <option value="current">현재</option>
                <option value="previous">직전</option>
                <option value="next">다음</option>
              </select>
              <select
                value={options.source.subtype}
                onChange={(e) => updateLessonSource({ subtype: e.target.value as 'main' | 'flash' })}
                className="px-2 py-1 text-xs border border-gray-300 rounded"
              >
                <option value="main">main</option>
                <option value="flash">flash (반짝)</option>
              </select>
            </div>
          )}

          {options.source.type === 'url' && (
            <input
              type="text"
              placeholder="https://..."
              value={options.source.url}
              onChange={(e) => update({ source: { type: 'url', url: e.target.value } })}
              className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <div className="text-xs text-gray-500 mb-1">Skip 허용</div>
          {(['always', 'after_first_watch'] as const).map((v) => (
            <label key={v} className="flex items-center gap-1.5 text-xs">
              <input type="radio" name={`skip-${component.id}`} checked={options.skipPolicy === v} onChange={() => update({ skipPolicy: v })} />
              {v === 'always' ? '항상' : '2회차부터'}
            </label>
          ))}
        </div>

        <div>
          <div className="text-xs text-gray-500 mb-1">배속 허용</div>
          {(['always', 'after_first_watch'] as const).map((v) => (
            <label key={v} className="flex items-center gap-1.5 text-xs">
              <input type="radio" name={`speed-${component.id}`} checked={options.speedPolicy === v} onChange={() => update({ speedPolicy: v })} />
              {v === 'always' ? '항상' : '2회차부터'}
            </label>
          ))}
        </div>

        <div>
          <div className="text-xs text-gray-500 mb-1">완료 기준</div>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              max={100}
              value={options.requiredWatchPercent}
              onChange={(e) => update({ requiredWatchPercent: parseInt(e.target.value) || 0 })}
              className="w-14 px-2 py-1 text-xs border border-gray-300 rounded"
            />
            <span className="text-xs">%</span>
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={options.autoPlay ?? false}
            onChange={(e) => update({ autoPlay: e.target.checked })}
          />
          자동 재생 (진입 시 바로 재생)
        </label>
      </div>
    </div>
  )
}
